/**
 * 🌍 Localisation (v2.88, per Javi) — English, Spanish, French.
 *
 * Deliberately NOT @nuxtjs/i18n: the module pulls in routing strategies,
 * SEO head management and lazy message loading, none of which a
 * client-rendered single-page game with `ssr: false` benefits from. A
 * flat key map plus one composable is ~40 lines and has no build cost.
 *
 * Keys are dot-namespaced by screen (`menu.*`, `draft.*`, `shop.*`).
 * English is the SOURCE and the fallback: a key missing from `es`/`fr`
 * renders its English string rather than the raw key, so a partial
 * translation degrades into a mixed-language UI instead of gibberish.
 * `npm run audit:i18n` reports which keys are still missing.
 *
 * SCOPE: this covers UI chrome. Gameplay CONTENT — creature names,
 * passive and relic descriptions, boss skills — is a separate ~373-string
 * surface where a mistranslated mechanic actively misleads the player, so
 * it stays English until translated with review. See §i18n in the repo
 * notes for the staging plan.
 */
import { COMPLIANCE_MESSAGES } from '~/data/complianceMessages'
import { POLICY_MESSAGES } from '~/data/policyMessages'

export const LOCALES = ['en', 'es', 'fr'] as const
export type Locale = (typeof LOCALES)[number]

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
}

/** Kept for reference only — the picker uses endonyms. Windows renders
 * regional-indicator pairs as literal letters ("GB"), and a flag is the
 * wrong symbol for a language regardless. Do not put these in the UI. */
export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
}

export type Messages = Record<string, string>

const en: Messages = {
  ...COMPLIANCE_MESSAGES.en,
  'account.title': 'Account',
  'account.guest': 'Guest',
  'account.deletedBody': 'This account was deleted; this device is now signed out and its saved game data has been cleared.',
  'account.deletedCleanupPending': 'This account was deleted; finish clearing this device before returning to the game.',
  ...POLICY_MESSAGES,
  // Native launch gate. Registered here so audit:i18n tracks English fallback coverage.
  'updateRequired.gameTitle': 'Elemental Dispatch',
  'updateRequired.title': 'Update required',
  'updateRequired.body': 'A new dispatch awaits. Update the game to continue your adventure.',
  'updateRequired.store': 'Update game',
  'updateRequired.retry': 'Retry',
  'updateRequired.checking': 'Checking for updates…',
  // ---- Title menu ----
  'menu.startRun': 'Start Run',
  // 🎓 v7.40 (per Javi, D3): the welcome card for a first-timer (~18 words).
  'welcome.title': 'Hey, welcome!',
  'welcome.body': 'Looks like it’s your first time playing. Want a quick run-through?',
  'welcome.yes': 'Yes, show me! →',
  'welcome.playedBefore': 'I’ve played before',
  'welcome.close': 'Close',
  // 🎓 v7.40 (D4): the Shop door's note when it first appears for a newcomer.
  'title.shopNote': 'You have {n} Stardust to spend!',
  'menu.featured': 'Creature of the Day',
  'menu.ascUnlocked': 'Ascension Unlocked!',
  'menu.ascNewSkills': 'New skills join the curriculum',
  'menu.ascContinue': 'Onward',
  'title.emodList': 'Elite Modifiers',
  'title.emodNote': 'Every elite node carries ONE of these, previewed on the map. No modifier repeats within an act.',
  'coll.modsTitle': 'Mission Modifiers',
  'coll.legacyHint': 'Something ancient stirs. Own every creature to awaken what remains.',
  'coll.legacyProgress': 'Owned: {n} / {total}',
  'coll.triCounts': '{m} mastered · {s} seen / {t}',
  'timeline.title': 'Dispatch Log',
  'timeline.enRoute': 'en route',
  'timeline.fighting': 'engaged',
  'timeline.succeeded': 'done',
  'timeline.failed': 'failed',
  'timeline.expired': 'expired',
  'timeline.nextIn': 'next in {n}s',
  'menu.ascUnlocks': 'Unlocks:',
  'menu.ascDetails': 'Tap for details',
  'menu.achUnlocked': 'Achievement earned!',
  'menu.legacyName': 'LEGACY',
  'menu.legacyStory': 'Every creature owned. The oldest of them all wakes, and walks to your basecamp. It will grow with every burden you hand it.',
  // 🎩 v7.78: under a homage awakening's reveal (English only until Javi asks for es/fr).
  'menu.homageJoinOne': '{name} joins your collection, ready for your next draft.',
  'menu.homageJoinPair': '{a} and {b} join your collection, ready for your next draft.',
  'menu.latestUpdate': 'Latest Update',
  'menu.newRun': 'New Run',
  'menu.preparingRun': 'Preparing run…',
  'menu.startFailed': 'Could not start your run. Please try again.',
  'menu.starweaveOffline': 'Your Starweave could not be reached. Retry once you are back online, or set out without your stars for this run.',
  'menu.starweaveRetry': 'Retry',
  'menu.starweaveStartWithout': 'Start without stars',
  'menu.starweave': 'Starweave',

  // ✨ Starweave modal (v6.84: keyed; talent names/descriptions are CONTENT).
  'starweave.title': 'Starweave',
  'starweave.backToProfile': 'Back to Profile',
  'starweave.profile': 'Profile',
  'starweave.stardust': 'Stardust',
  'starweave.costRule': 'Every star raises all prices by {pct}%.',
  'starweave.mapZoomed': 'Constellation. Pinch or scroll to zoom, drag to explore.',
  'starweave.mapFull': 'Your full constellation. Pinch or scroll to zoom in.',
  'starweave.heart': 'Your permanent constellation',
  'starweave.zoomFit': 'Fit whole constellation',
  'starweave.zoomIn': 'Zoom in on constellation',
  'starweave.zoomInShort': 'Zoom in',
  'starweave.inspector': 'Selected talent',
  'starweave.details': 'Talent details',
  'starweave.nova': 'NOVA',
  'starweave.tier': 'TIER {n}',
  'starweave.retry': 'Retry',
  'starweave.owned': 'Part of your constellation',
  'starweave.toAwaken': 'Stardust to awaken',
  'starweave.priceTitle': '{amount} stardust',
  'starweave.awakenedCheck': 'Awakened ✓',
  'starweave.note': 'Stardust bound to your stars returns when you unbind them. Your current expedition keeps the stars it set out with; changes apply to your next new run. Daily and puzzle runs exclude Starweave.',
  'starweave.unbind': 'Unbind',
  'starweave.unbindAll': 'Unbind all {n} stars and reclaim {amount} stardust',
  'starweave.unbindNone': 'Unbind: no stars bound',
  'starweave.stateAwakened': 'Awakened',
  'starweave.stateAvailable': 'Available',
  'starweave.stateLocked': 'Locked',
  'starweave.weaving': 'Weaving…',
  'starweave.connectionUnavailable': 'Connection unavailable',
  'starweave.loading': 'Loading Starweave…',
  'starweave.starAwakened': 'Star awakened',
  'starweave.requiresLinked': 'Requires linked stars',
  'starweave.moreStardust': 'More stardust needed',
  'starweave.igniteNova': 'Ignite Nova',
  'starweave.awakenStar': 'Awaken star',
  'starweave.err.funds': 'Not enough stardust. Any pending earnings must finish syncing first.',
  'starweave.err.requirements': 'Awaken the connected stars first.',
  'starweave.err.alreadyOwned': 'This star is already awakened.',
  'starweave.err.nothingOwned': 'There are no stars to unbind.',
  'starweave.err.runActive': 'Your current expedition set out with these stars. Finish or abandon it to unbind them.',
  'starweave.err.busy': 'Your previous change is still finishing.',
  'starweave.err.signin': 'Connect your player account to purchase stars.',
  'starweave.err.offline': 'Could not reach your stardust account. Reconnect and retry.',
  'starweave.err.conflict': 'Your constellation changed. Review the updated price and try again.',
  'starweave.err.generic': 'Could not update Starweave. Refresh your account and try again.',
  'starweave.awakenedFor': '{name} awakened for future runs. All remaining stars now cost {pct}% more.',
  'starweave.novaAlso': 'Other Novas also cost {mult}× more.',
  'starweave.reclaimed': '{amount} stardust reclaimed. Your constellation is cleared for future runs and prices are back to their starting values.',

  // 🗺️ Route panel (v6.78-80 redesign, keyed v6.84): map + draft scouting.
  'route.chooseDestination': 'Choose a destination',
  'route.plaqueAlt': 'Route preview',
  'route.scoutTitle': 'Scout the route',
  'route.chooseTitle': 'Choose your next destination',
  'route.scrollbar': 'Scroll through the route',
  'route.selected': 'Selected destination: {name}',
  'route.inspect': 'Inspect {name}',
  'route.facts': 'Destination requirements and rewards',
  'route.bossReward': '1 training reward + 1 relic · Epic or better',
  'route.bossSlots': '{slots} slots · {xp} XP',
  'route.moreFacts': 'Scroll for more details ↓',
  'route.entering': 'On our way…',
  'route.takePath': 'Take this path',
  'route.mapRegion': 'Route map. Scroll to inspect destinations ahead.',
  'route.scrollUp': 'Scroll route up',
  'route.scrollDown': 'Scroll route down',
  'route.completed': 'Completed',
  'route.availableNext': 'Available next',
  'route.furtherAhead': 'Further ahead',

  // 🏁 Act completion (v6.78 redesign, keyed v6.84).
  'act.noWeeklyStardust': 'Weekly runs award no act stardust',
  'act.yoursBeyond': 'Yours beyond this run',
  'act.yoursBeyondAsc': 'Yours beyond this run · A{n} ×{mult}',
  'act.weeklyExpedition': 'Weekly expedition',
  'act.gauntlet': 'Gauntlet',
  'act.expedition': 'Expedition',
  'act.ascension': 'Ascension {n}',
  'act.normal': 'Normal',
  'act.onward': 'Onward to Act {n}',
  'act.returnMap': 'Return to the map',
  'act.everyChapter': 'Every chapter complete',
  'act.ahead': 'Ahead: the {theme}',
  'act.oneRemains': 'One challenge remains',
  'act.missionsCompleted': 'Missions completed',
  'act.missionsFailed': 'Missions failed',
  'act.missionsExpired': 'Missions expired',
  'act.damageTaken': 'Damage taken',
  'act.goldEarned': 'Gold earned',
  'act.announce': 'Act {act} complete. {n} missions completed. {payout} stardust earned.',
  'act.victoryAlt': 'Victory',
  'act.eyebrowTale': 'A tale worth remembering',
  'act.eyebrowHorizon': 'A new horizon',
  'act.eyebrowChapter': 'A chapter written in the stars',
  'act.forestSaved': 'The forest is saved',
  'act.nextTitle': 'Act {n} · The {theme}',
  'act.expeditionComplete': 'Expedition complete',
  'act.actComplete': 'Act {n} complete',
  'act.subtitleRest': 'Your company has earned its rest.',
  'act.subtitleNext': 'The next chapter awaits.',
  'act.subtitleFinal': 'The final boss has fallen. Take a bow.',
  'act.subtitleBoss': 'The boss has fallen. Catch your breath.',
  'act.recap': 'Act recap',
  'act.favorite': 'Favorite creature',
  'act.dispatches': '{n} dispatches',
  'act.stardustEarned': 'Stardust earned',
  'act.stardustSr': '{n} stardust',
  'act.thisAct': 'This act',
  'act.progress': 'Expedition progress',
  'act.actN': 'Act {n}',
  'act.complete': 'Complete',
  'act.next': 'Next',
  'act.aheadShort': 'Ahead',
  'act.campAlt': 'Your company\'s camp',
  'act.home': 'Home, with a story to tell.',
  'act.together': 'Onward, together.',
  'act.scrollHint': 'Scroll for the full recap ↓',
  'act.turning': 'Turning the page…',
  'act.bask': 'Bask in glory',

  // ✨ Starweave talents in play (v6.77, keyed v6.84).
  'rewardui.collectorTitle': 'Master Collector · Choose one extra relic',
  'rewardui.collectorSub': 'Your extra relic is free. Available once per act.',
  'rewardui.secondLook': 'Second Look · Reroll relics',
  'rewardui.collectorSkip': 'Skip · Save for another offer',
  'detail.skillReroll': 'Skill Reroll · Refresh choices',
  'detail.doubleLesson': 'Broader Curriculum: this class can teach two skills. Pick a second one.',
  'levelup.trainsOnly': 'Trains only {elements}',
  'mission.composureSaved': 'Composure! Mr. Khool asked the dice again.',
  'mission.overkillPct': 'Coverage {n}% · Overkill at {at}%',
  'coll.homageHint': 'Not yet awakened.',
  'menu.starweaveLocked': 'Unlocks at Ascension {n}',

  // 🏆 Achievements folio chrome (v6.78; folded in from achievementFolioCopy.ts in v6.84).
  'ach.folio.all': 'All',
  'ach.folio.tagline': 'Every adventure leaves a mark.',
  'ach.folio.filters': 'Filter achievements',
  'ach.folio.medals': 'Achievement medals',
  'ach.folio.inspect': 'Inspect achievement',
  'ach.folio.selected': 'Selected achievement',
  'ach.folio.unknown': 'Unknown deed',
  'ach.folio.undiscovered': 'Undiscovered',
  'ach.folio.secret': 'Secret achievement',
  'ach.folio.secretWaits': 'A secret waits',
  'ach.folio.mystery': 'Some stories reveal themselves only after you have lived them.',
  'ach.folio.remembered': 'A deed remembered',
  'ach.folio.unlocked': 'Achievement unlocked',
  'ach.folio.progress': 'Your progress',
  'ach.folio.completed': 'Completed',
  'ach.folio.earnedOn': 'Earned',
  'ach.folio.yours': 'Yours to wear',
  'ach.folio.unlocks': 'Unlocks',
  'ach.folio.concealed': 'Reward concealed',
  'ach.folio.surprise': 'A surprise for the journey.',
  'ach.folio.title': 'Title',
  'ach.folio.avatar': 'Avatar',
  'ach.folio.noEarned': 'Your first deed is still ahead of you.',
  'ach.folio.allEarned': 'Every deed in this collection is earned.',
  'ach.folio.footer': 'Earned automatically. Remembered always.',

  // 🌲 Act themes (v6.84): the act recap names the next region.
  'theme.forest': 'Forest',
  'theme.desert': 'Desert',
  'theme.tundra': 'Tundra',
  'theme.hell': 'Hell',
  'menu.continueRun': 'Continue Run',
  'menu.abandonRun': 'Abandon Run',
  'menu.shop': 'Shop',
  'menu.leaderboard': 'Leaderboard',
  'menu.puzzle': 'Puzzle Game',
  'menu.collection': 'Collection',
  'menu.profile': 'Profile',
  'menu.achievements': 'Achievements',
  'menu.changelog': 'What’s New',
  'menu.language': 'Language',
  'menu.ascension': 'Ascension',
  'menu.ascLocked': 'Locked — win your highest level to climb',
  'menu.ascRaise': 'Raise ascension',
  'menu.ascLower': 'Lower ascension',

  // ---- Shop ----
  'shop.title': 'Stardust Shop',
  'shop.balance': 'Balance',
  'shop.restockIn': 'New stock in {time}',
  'shop.owned': 'Owned',
  'shop.confirm': 'Confirm?',
  'shop.recruit': 'Recruit',
  'shop.confirmPurchase': 'Confirm purchase?',
  'shop.note': 'Tap a creature to inspect it. Purchases apply to runs started afterwards.',
  // 🏦 v4.92: purchases confirm with the wallet server and fail closed.
  'shop.walletSyncing': 'Your stardust is still syncing — try again in a moment.',
  'shop.walletOffline': 'Couldn’t reach the shop — check your connection and try again.',
  'shop.walletShort': 'Not enough stardust.',
  // 👀 v6.50: the storefront is on show before the till opens.
  'shop.packsSoonChip': 'SOON',
  'shop.packsSoon': 'Stardust packs are coming soon — nothing here can be bought yet.',
  // 💳 v4.94: real-money stardust via Stripe checkout.
  'shop.getStardust': 'Get Stardust',
  'shop.purchaseProcessing': 'Confirming your purchase…',
  'shop.purchaseConfirmed': '+{n} stardust added to your vault!',

  // ---- Draft ----
  'draft.answeredCall': '{n} answered today’s call — pick any {pick}',
  'draft.yourCollection': 'Your Collection — always ready',
  'draft.yourTeam': 'Your Team',
  'draft.confirmTeam': 'Confirm Team',
  'draft.continueToMap': 'Continue to Map',
  'draft.chooseRelic': 'Choose a Starting Relic',
  'draft.routePreview': 'Route Preview',
  'draft.planRoute': 'Plan route',
  'draft.doneDrawing': 'Done drawing',
  'draft.clearPlan': 'Clear plan',
  'draft.filterAll': 'All',
  'draft.filterFavorites': 'Favorites',
  'draft.inspect': 'Inspect',
  'draft.captain': 'Captain',
  'skillmeta.once': 'Once per node.',
  'skillmeta.cooldown': 'Recharges in {n}s.',

  // ---- Collection ----
  'collection.title': 'Collection',
  'collection.creatures': 'Creatures',
  'collection.enemies': 'Enemies',
  'collection.bosses': 'Bosses',
  'collection.relics': 'Relics',
  'collection.skills': 'Trained Skills',
  'collection.rewards': 'Rewards',
  'collection.owned': 'owned',
  'collection.met': 'met',
  'collection.undiscovered': 'Not yet discovered',
  'collection.hint': 'Everything you’ve met fills in — tap a found entry for its dossier. Creatures you’ve only borrowed for a run stay in black and white until you buy them.',
  'collection.favorite': 'Favorite this creature',
  'collection.unfavorite': 'Remove from favorites',
  'collection.back': 'Back',

  // ---- Encounter ----
  'encounter.basecamp': 'Basecamp',
  'encounter.yourTeam': 'Your Team',
  'encounter.empty': 'Empty',
  'encounter.fight': 'Fight',
  'encounter.required': 'Required',
  'encounter.teamTotal': 'TEAM TOTAL',
  'encounter.winChance': 'Win chance',
  'encounter.surge': 'Surge',
  'encounter.armed': 'Armed!',
  'encounter.used': 'Used',
  'encounter.gold': 'Gold',

  // ---- Generic ----
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.back': 'Back',
  'common.level': 'Lv',

  // ---- 🌍 v5.18: the full-game translation pass. Everything below was
  // hardcoded English in templates until the audit swept it out. ----
  'common.select': 'Select',
  'common.continue': 'Continue',
  'common.done': 'Done',
  'common.loading': 'Loading…',
  'common.tapToContinue': 'Tap to continue',
  'common.paused': 'Paused',
  'common.free': 'Free',
  'common.or': 'or',
  'common.and': 'and',
  'common.menu': 'Menu',
  'common.on': 'On',
  'common.off': 'Off',
  'common.gotIt': 'Got it',
  'common.letsGo': 'Let’s go!',
  'common.act': 'Act {n}',
  'common.lvN': 'Lv {n}',

  // ---- Elements (the most-repeated nouns in the game) ----
  'element.fire': 'Fire',
  'element.water': 'Water',
  'element.grass': 'Grass',
  'element.lightning': 'Lightning',
  'element.earth': 'Earth',

  // ---- Rarity + node-tier ladders ----
  'rarity.common': 'Common',
  'rarity.uncommon': 'Uncommon',
  'rarity.rare': 'Rare',
  'rarity.epic': 'Epic',
  'rarity.legendary': 'Legendary',
  'tier.easy': 'Easy',
  'tier.medium': 'Medium',
  'tier.hard': 'Hard',
  'tier.elite': 'Elite',
  'tier.boss': 'Boss',

  // ---- Node previews (data lives in nodes.ts; text lives here) ----
  'node.heading': '{label} Node',
  'node.swarm.label': 'Swarm',
  // 🎓 v7.38 (per Javi): no "low damage" - one swarm spawn in four rolls medium tier by design.
  'node.swarm.blurb': 'Many quick missions · short timers',
  'node.swarm.reward': 'Choose 1 training reward',
  'node.mixed.label': 'Mixed',
  'node.mixed.blurb': 'Fast and slow threats · varied elements',
  'node.mixed.reward': 'Choose 1 training reward',
  'node.titan.label': 'Titan',
  'node.titan.blurb': 'Few huge missions · high requirements · heavy damage',
  'node.titan.reward': 'Choose 1 reward · richer XP',
  'node.elite.label': 'Elite',
  'node.elite.blurb': 'Punishing missions · stronger foes',
  'node.elite.reward': '1 training reward + 1 relic',
  'node.rest.label': 'Rest',
  'node.rest.blurb': 'A safe place to recover',
  'node.rest.reward': 'Choose 1 recovery option',
  'node.chest.label': 'Chest',
  'node.chest.blurb': 'An unguarded prize',
  'node.chest.reward': 'Choose 1 of 3 relics',
  'node.shop.label': 'Shop',
  'node.shop.blurb': 'A traveling merchant · spend your gold',
  'node.shop.reward': 'Buy up to 6 random relics',
  'node.boss.label': 'Boss',
  'node.boss.blurb': 'One colossal final battle',
  'node.boss.reward': 'Advance the run',
  'node.mystery.label': 'Mystery',
  'node.mystery.blurb': 'Anything could happen…',
  'node.mystery.reward': 'Unknown',
  'node.ambush.label': 'Ambush',
  'node.ambush.blurb': 'Enemies leap from the shadows!',
  'node.ambush.reward': 'Choose 1 training reward',
  'node.puzzle.label': 'Puzzle',
  'node.puzzle.blurb': 'All missions at once, mirrored from YOUR team · no timers · each creature fights once',
  'node.puzzle.reward': 'Find the intended split to earn a relic; otherwise less damage = better rewards',
  'node.defeatN': 'Defeat {n} missions',
  'node.defeatOne': 'Defeat 1 mission',
  'node.trialsRule': 'Resolve every trial — one team each',
  'node.trialsMirror': '🪞 Trials mirror YOUR team’s stats',
  'node.bossExact': 'The boss, exactly:',
  'node.bossCoverage': '· coverage = win chance; missing coverage = damage taken',

  // ---- Reward screen flow ----
  'rewardui.chooseRelic': 'Choose a Relic',
  'rewardui.chooseRest': 'Choose How to Rest',
  'rewardui.chooseReward': 'Choose a Reward',
  'rewardui.stage': 'Reward {n} of {m}',
  'rewardui.chooseCreature': 'Choose a Creature',
  'rewardui.chooseCreatures': 'Choose {n} Creatures',
  'rewardui.xpEach': '+{n} XP each',
  'rewardui.tapCreatureNote': 'Tap a creature in Your Team below — tap again to unpick.',
  'rewardui.tapCreaturesNote': 'Tap creatures in Your Team below — tap again to unpick.',
  'rewardui.nobodyPicked': 'nobody picked yet',
  'rewardui.confirmCount': 'Confirm ({n} / {m})',
  'rewardui.chooseSkill': 'Choose a Skill to Teach',
  'rewardui.chooseStudent': 'Choose the Student',
  'rewardui.learnedForever': '{name} — learned instantly, forever',
  'rewardui.tapStudentNote': 'Tap the student in Your Team below.',
  'rewardui.noStudent': 'no student picked yet',
  'rewardui.declineTraining': 'Decline the training',
  'rewardui.chooseRecruit': 'Choose Your Recruit',
  'rewardui.joinsAt': 'Joins at Lv {n} — stats shown are final',
  'rewardui.recruit': 'Recruit',
  'rewardui.declineRecruit': 'Decline — keep your current team',
  // 🙅 v7.78: walk away from an in-run relic pick (English only until Javi asks for es/fr).
  'rewardui.declineRelic': 'Take nothing',
  'rewardui.claimed': 'Rewards claimed!',
  'rewardui.healFull': 'Heal all of your HP ({n} HP).',
  'rewardui.healHalf': 'Heal 50% of your max HP ({n} HP).',

  // ---- Resolution overlay ----
  'resolution.trainingDone': 'Training complete!',
  'resolution.sideJobDone': 'Side job done!',
  'resolution.splits': 'It splits apart!',
  'resolution.pieceDown': 'Piece destroyed!',
  'resolution.windfall': 'WINDFALL!',
  'resolution.bossDown': 'Boss defeated!',
  'resolution.success': 'Success!',
  'resolution.bossEndures': 'The boss endures!',
  'resolution.failed': 'Failed!',
  'resolution.skillLearned': '🎓 A new skill is learned — no ⭐ goal credit',
  'resolution.goldPaid': '💰 +{n} gold — no ⭐ goal credit',
  'resolution.basaltWeakensBy': 'Basalt weakens: −{n} {element} — no ⭐ goal credit',
  'resolution.basaltWeakens': 'Basalt weakens — no ⭐ goal credit',
  'resolution.vexSquashed': 'Dr. Vex is squashed for now — no ⭐ goal credit',
  'resolution.piecesMustFall': 'The pieces must ALL fall to win the node',
  'resolution.nodeFallsWhen': 'The node falls when every piece is down',
  'resolution.feint': '🎭 The feint! {icon}{n} scattered into {gains}',
  'resolution.xpEach': '+{n} XP each',
  // 🎲 v7.79: a won Wager's flat purse (English only until Javi asks for es/fr).
  'resolution.wagerWon': 'The Wager pays +{n} gold',
  // 🎩 v7.79: Composure on the wheel - the pending line while the arm spins again, and the screen-reader note.
  'resolution.composureAgain': 'Composure: rolling again',
  'resolution.composureRoll': 'Composure: roll {n} of {max}',
  'resolution.composureSr': 'Missed. Mr. Khool keeps his composure: roll {n} of {max}.',
  'resolution.missingCoverage': '−{n} HP — missing coverage',
  'resolution.damage': '−{n} HP',
  'resolution.noDamage': 'No damage',
  'resolution.bossAwaits': 'The boss awaits another challenge…',
  'resolution.injuriesPrevented': 'Injuries prevented',
  'resolution.teamInjured': 'Team injured',
  // 🎓 v7.41 (per Javi, §3 C + D2): a first run's failure card says why, in
  // plain words - element names written out, health (never HP).
  'resolution.whyShort': 'Not enough {elements}.',
  'resolution.whyLuck': 'Bad luck this time.',
  'resolution.whyLost': 'You lost {n} health.',
  'resolution.whyNoLoss': 'You lost no health.',
  // 🎓 v7.41 (per Javi, §3 C - F14): the kind defeat, until location 1 is cleared.
  'defeat.toughStart': 'Tough start!',
  'defeat.causeFast': 'Fast mode was on a lot.',
  'defeat.causeNoElement': 'Your team had no {element}.',
  'defeat.causeLowElement': 'Your team needed more {element}.',
  'defeat.causeGotAway': 'Monsters got away.',
  'defeat.causeFailed': 'Some fights were lost.',
  'defeat.causeWon': 'Some wins still cost health.',
  'defeat.causeTip': 'Pick creatures that match the location.',
  'defeat.tryAgain': 'Try again',
  'defeat.replayPractice': 'Replay the practice',
  'defeat.backToTitle': 'Back to title',

  // ---- Roster bar / team dock ----
  'roster.pick': 'Pick…',
  'roster.arm': 'Arm',
  'roster.revert': 'Revert',
  'roster.equalize': 'Equalize',
  'roster.reforgeTitle': 'Reforge — {name}',
  'roster.reforgeRule': 'Redistribute all {n} points. This is permanent.',
  'roster.unspent': 'Unspent: {n}',
  'roster.onMission': 'On a mission',
  'roster.injuredTip': 'Injured: −{n}% to all elements',
  'roster.injured': 'Injured −{n}%',
  'roster.spendPoints': 'Spend {n} stat points now',
  'roster.spendPoint': 'Spend 1 stat point now',
  'roster.inspectTip': 'Inspect — skills, passives & stats',
  'roster.xpTip': '{xp} / {next} XP to next level',

  // ---- Mission detail ----
  'mission.paused': 'Paused',
  'mission.requires': 'Requires',
  'mission.sFight': '{n}s fight',
  'mission.windfallPct': '{n}% windfall',
  'mission.bloodPact': 'Blood Pact: −{hp} HP → +{pct}%',
  'mission.sealed': ' · SEALED',
  'mission.grudge': 'Grudge: the +20% is already counted in the chance above.',
  'mission.bonusContract': 'Bonus contract: pays {n} gold on success — doesn’t count toward the node goal, and letting it slip away is free.',
  'mission.trainingRule': 'Training Grounds: send ONE creature on a long training session — no roll, no damage. Survive the node and the skill is theirs forever.',
  'mission.strikeRule': 'Coordinated strike: lock a team into EVERY piece, then Strike as One below the field. The fuses keep burning while you plan — commit fast, or a piece reforms and its team walks free.',
  'mission.trialRule': 'Trial: each creature can join only ONE trial. Lock in a team for every trial, then Begin the Trials — find the intended split to earn a relic; otherwise total damage taken sets your reward.',
  'mission.bossRule': 'Boss: your coverage IS your win chance — and win or lose, you take the missing share as damage',
  'mission.bossRuleNow': ' (currently −{n} HP)',
  'mission.missBites': 'Whatever coverage you miss bites win or lose. At 100% you take nothing.',
  'mission.missBitesNow': 'The missing {pct}% bites win or lose: −{n} HP. At 100% you take nothing.',
  'mission.missShort': '−{n} HP',
  'mission.blackoutTip': 'The mists hide your odds here — commit the team and find out.',
  'mission.fogTip': 'The fog hasn’t lifted — this mission’s demands surface after a few seconds on the field.',
  'mission.ironNote': 'Iron Rules: this team WILL prevail — any missing coverage bites as damage.',
  'mission.ironBoss': 'Iron Rules: the boss falls only to FULL coverage.',
  'event.chooseTarget': 'Choose a creature',
  'event.chooseSecond': 'Choose a second creature',
  'event.chooseElement': 'Choose an element',
  'event.back': 'Back',
  'event.walkAway': 'Walk away empty-handed',
  'event.continue': 'Continue',
  // 🔍 v7.39 (per Javi): the tale can show the whole team, and the picker previews its outcome.
  'event.viewTeam': 'View your team',
  'event.picked': 'Picked',
  'event.previewXp': '+{n} XP',
  'event.previewTip': 'What this choice does to {name}',
  'mission.fullyCovered': 'Fully covered — this mission cannot hurt you, win or lose.',
  'mission.overcommitted': 'Overcommitted by {n} — a failure deals +{dmg} extra damage.',
  'mission.faust': 'Faust offers three bargains — you must take one:',
  'mission.lockedTeam': 'Locked team',
  'mission.teamTotal': 'Team total',
  'mission.pickTeam': 'pick your team',
  'mission.needs': 'Needs',
  'mission.team': 'Team',
  'mission.clearTeam': 'Clear Team',
  'mission.lockIn': '🔒 Lock In',
  'mission.beginTraining': 'Begin Training',
  'mission.decree': 'Decree!',
  'mission.fight': 'Fight!',
  'mission.knowsIt': 'Knows it',
  'mission.cantLearn': 'Can’t learn',
  'mission.spent': 'Spent',
  'mission.committed': 'Committed',
  'mission.discardCost': '−{n} HP',
  'mission.eclipsed': '{element} is eclipsed — your creatures add NOTHING to it. Moves in {n}s. Strike when the cheapest element is dark.',
  'mission.growing': '{element} grows +1 in {n}s (target rotates in {m}s). It grows even while you’re away — commit fast.',
  'mission.corrupted': '{element} is corrupted: every point you commit OVER its requirement deals {n} extra damage if the attempt fails.',
  'mission.cursedSlot': 'The first slot is cursed: your FIRST pick loses its strongest relevant element for this fight. Feed it something expendable.',
  'mission.cursedSlots': 'The first {n} slots are cursed: each of those picks loses its strongest relevant element for this fight. Feed them something expendable.',
  'mission.sandmanN': 'Each attempt it knocks out {n} random allies — they can’t join that fight.',
  'mission.feintArmed': '{element} is unstable: when the fight starts, its whole requirement scatters onto the other elements. Overkill elsewhere to hedge.',
  'mission.feintSprung': 'The feint has been sprung — the requirements are now what they are.',
  'mission.fragment': 'A fragment of the fractured boss. Beat every fragment to win the node.',
  'mission.fractures': 'Beating it splits it into fragments that inherit 90% of the requirements — focused specialists beat rainbow spreads here.',
  'mission.sandman': 'Each attempt it knocks out a random ally — they can’t join that fight.',
  'mission.sunderedHalf': 'Half of the Sundered Whole — any overkill you commit here strengthens its twin. Precision beats force.',
  'mission.sunders': 'When beaten, it sunders into Yin and Yang — two timed halves that feed on each other’s overkill.',
  'mission.amalgam': 'The Amalgam is a long grind — and Dr. Vex keeps meddling with quick side jobs. Squash them or they chip your HP.',
  'mission.blood': 'Grossly overgrown, but it accepts blood: pay {hp} HP to shave {cut} points off a requirement (buttons below).',
  'mission.chooseCurse': 'Choose your curse before the fight.',
  'mission.basalt': 'A slow colossus — smash the Power Crystals around it to drain −2 from its strongest element, even while your team is mid-fight.',
  'mission.curseSolitude': 'Curse of Solitude: −5% success for every creature beyond the first.',
  'mission.curseDoubt': 'Curse of Doubt: success capped at 75% — failures deal +10 extra damage.',
  'mission.curseExcess': 'Curse of Excess: +{n} to EVERY element — failure damage is tripled.',
  'mission.bloodCutBtn': '−{hp} HP: −{cut} {element}',
  'mission.bloodPerCut': '−{n} per cut',

  // ---- Forest board ----
  'forest.enRoute': 'team en route',
  'forest.lockedIn': 'Locked in · {n}%',
  'forest.goldBonus': '+{n} gold · bonus',
  'forest.now': 'now: ',
  'forest.expiryDamage': '−{n} HP',
  'forest.ffOn': '⏩ ×3 — tap for normal speed',
  'forest.ffOff': '⏩ 3×',
  'forest.upTo': 'Up to {n} creatures',

  // ---- Encounter page ----
  'encounter.escalation': '+{n}% Damage',
  'encounter.escalationTip': 'New missions hit harder the longer this node runs',
  'encounter.ffBanner': '⏩ ×3 SPEED — tap an empty spot to stop',
  // 🎓 v7.40 (per Javi, D6b): a first run's ×3 lives on the banner's ⏩ button.
  'encounter.pausedBig': 'PAUSED',
  'encounter.tapResume': 'tap anywhere to resume',
  'encounter.pauseTip': 'Pause the field',
  'encounter.trialsComplete': 'Trials Complete',
  'encounter.perfectRelic': 'PERFECT SPLIT — a relic awaits below.',
  'encounter.scoreDamage': 'Score: {n} damage taken',
  'encounter.lowerBetter': '(lower is better — the intended split earns a relic)',
  'encounter.revealSplit': 'Reveal the intended split',
  'encounter.mirroredFrom': 'The trials were mirrored from these groups (plus a little excess).',
  'encounter.levelComplete': 'Level {n} complete',
  'encounter.perfect': 'PERFECT — not a scratch.',
  'encounter.damageTaken': '{n} damage taken',
  'encounter.zeroPerfect': '(0 is perfect — replay any level to improve its best)',
  'encounter.firstClear': '+{n} Stardust banked — first clear of level {m}.',
  'encounter.nextLevel': 'Next level ({n})',
  'encounter.backToPuzzles': 'Back to the Puzzle List',
  'encounter.beginTrials': 'Begin the Trials!',
  'encounter.lockEveryTrial': 'Lock in every trial ({n} / {m})',
  'encounter.strikeAsOne': 'Strike as One!',
  'encounter.lockEveryPiece': 'Lock in every piece ({n} / {m}) — the fuses are burning!',
  'encounter.runLost': 'Run Lost',
  'encounter.runLostBody': 'Your HP reached 0. The ruins claimed this expedition.',
  'encounter.fellIn': 'Fell in Act {act} · Nodes cleared this act: {n} · Final team: {team}',
  'encounter.endRun': 'End Run',
  'encounter.routeAhead': 'The Route Ahead',
  'encounter.backToFight': 'Back to the Fight',
  'encounter.mapTip': 'View the map (pauses)',
  'encounter.relicsTip': 'View your relics (pauses)',
  'encounter.damageN': '{n} damage',
  'encounter.puzzleN': 'Puzzle {n}',

  // ---- Map page ----
  'mappage.runWon': 'Run Won!',
  'mappage.runWonBody': 'The final boss has fallen and the Floating Ruins are safe. Your team stands victorious below.',
  'mappage.ascUnlocked': 'ASCENSIONS UNLOCKED!',
  'mappage.ascUnlockedBody': 'A ladder of ten harder climbs now awaits: each level piles a new hardship onto the run — injuries, thinner rations, elite gauntlets and worse — and beating one unlocks the next. Choose your Ascension on the title screen.',
  'mappage.nextAsc': '{name} unlocked — select it on the title screen for a harder climb.',
  'mappage.firstClear': 'First clear!',
  'mappage.firstClearMode': 'Stardust banked — paid once per mode, so the next climb pays its own bounty.',
  'mappage.firstClearAsc': 'Stardust banked — paid once per ascension level, so the next climb pays its own bounty.',
  'mappage.drawHint': 'Draw with a finger — use ⬆️⬇️ to move along the road',
  'mappage.drawHintPc': '🖱️ On PC, right-click-drag draws anytime',
  'mappage.confirmNode': 'Confirm Node',
  'mappage.notReachable': 'Not Reachable Yet',
  'mappage.tapGlowing': 'Tap a glowing node to preview it.',
  'mappage.youAreHere': 'You are here',
  'mappage.threats': 'Threats: {list}',

  // ---- The traveling merchant (in-run shop) ----
  'merchant.title': 'The Traveling Merchant',
  'merchant.tagline': '“Everything’s for sale, friend.” · You carry',
  'merchant.alwaysStocked': 'Always in stock',
  'merchant.trainingName': 'Training Ground',
  'merchant.trainingText': 'Pick 1 of 3 trained skills, then choose a creature — it learns instantly.',
  'merchant.companionName': 'New Companion',
  'merchant.companionText': 'Max roster +1. Choose 1 of 3 new creatures to join at your average level.',
  'merchant.rosterFull': 'Roster full',
  'merchant.locked': 'Locked',
  'merchant.needsAscension': 'Ascension 1+',
  'merchant.allSkillsLearned': 'All skills learned',
  'merchant.bare': 'The shelves are bare — you cleaned the merchant out.',
  'merchant.leave': 'Leave the Shop',

  // ---- Game menu ----
  'gamemenu.music': 'Music: {state}',
  'gamemenu.sfx': 'Sound effects: {state}',
  'gamemenu.musicVolume': 'Music volume',
  'gamemenu.sfxVolume': 'Sound effects volume',
  'gamemenu.muteMusic': 'Mute music',
  'gamemenu.unmuteMusic': 'Unmute music',
  'gamemenu.muteSfx': 'Mute sound effects',
  'gamemenu.unmuteSfx': 'Unmute sound effects',
  'gamemenu.tapFf': 'Tap to Fast Forward: {state}',
  // 🎭 v7.56: the face-off scenes before each act and each boss fight (English only, like tapFf).
  'gamemenu.storyScenes': 'Story scenes: {state}',

  // ---- 🎭 v7.56: the face-off scenes' chrome (English only; the lines themselves live in data/versusScript.ts) ----
  'versus.skip': 'Skip',
  'versus.aria': 'Face-off with {boss}. Tap to continue.',
  'versus.tag.shadow': 'a shadow',
  'versus.tag.mastermind': 'the mastermind',
  'versus.banner.open': 'ACT {n}',
  'versus.banner.boss': 'ACT {n} BOSS',
  'versus.banner.final': 'FINAL BOSS',
  'gamemenu.titleScreen': 'Title screen',
  'gamemenu.abandonBody': 'This run ends here — its team, relics and map progress are gone for good.',

  // ---- Relics modal ----
  'relicsmodal.title': 'Your Relics ({n})',
  'relicsmodal.empty': 'No relics yet — seek out chests and elites.',

  // ---- Stat help ----
  'stathelp.speedTitle': 'Speed',
  'stathelp.speedBody': 'How fast a creature walks to a mission — and back. A TEAM travels at its slowest member’s pace, so one slow bruiser drags the whole squad. Skim, Trailblazer and travel relics can skip the walk entirely.',
  'stathelp.fightTitle': 'Fight',
  'stathelp.fightBody': 'Shaves the fight timer once the team arrives: every point of Fight across the team cuts the fight time by 4%, down to a floor of −70%. Storm Tempo, Pacesetter, Momentum and Impulse all feed it.',

  // ---- Skill target picker ----
  'teampicker.refract': 'Select a creature to copy!',
  'teampicker.graft': 'Select a teammate to graft onto!',
  'teampicker.reforge': 'Select a creature to reforge!',
  'teampicker.mimic': 'Select a Surge to mimic!',
  'teampicker.fallback': 'Select a target!',

  // ---- Full-screen flashes ----
  'flash.rekindle': 'Rekindle! — Risen From the Ashes',
  'flash.safetyNet': 'Safety Net! — Damage Prevented',

  // ---- Team summary ----
  'teamsummary.coverage': 'Team Coverage',

  // ---- Run-sync conflict chooser ----
  'sync.title': 'Two runs in progress',
  'sync.body': 'This device and another one both played since they last agreed. Only one can continue — pick which.',
  'sync.keepLocal': 'Keep this device’s run',
  'sync.takeRemote': 'Take the other device’s run',
  'sync.acceptEnded': 'Let the run end',
  'sync.remoteEnded': 'That device finished, lost or abandoned this run',
  // 🪦 v6.38: either side may be a tombstone, and the card must say
  // which. A local ending has no run to "keep" - only one to end.
  'sync.endEverywhere': 'End the run everywhere',
  'sync.localEnded': 'This device finished, lost or abandoned this run',
  'sync.title.remoteEnded': 'The run ended elsewhere',
  'sync.body.remoteEnded': 'Another device finished, lost or abandoned this run, but this device also played since they last agreed. Continue here, or let the ending stand.',
  'sync.title.localEnded': 'This run ended here',
  'sync.body.localEnded': 'This device finished, lost or abandoned this run, but another device has kept playing it. End it everywhere, or take that device’s run.',
  'sync.title.bothEnded': 'This run has ended',
  'sync.body.bothEnded': 'Both devices ended this run. There is nothing left to choose between — confirm the ending.',
  'sync.summary': 'Act {act} · {hp}/{max} HP · {phase} · {n} creatures',
  'sync.justNow': 'played just now',
  'sync.discarded': 'The run you do not pick is discarded.',
  'sync.phase.draft': 'drafting',
  'sync.phase.map': 'on the map',
  'sync.phase.node': 'mid-node',
  'sync.phase.reward': 'taking a reward',
  'sync.phase.done': 'finished',
  // 🪦 v6.38: what summarizeRun reports for a side with no run. The
  // chooser no longer shows a statline for that side, but the word must
  // exist so it can never leak raw.
  'sync.phase.none': 'no run',
  'sync.agoS': '{n}s ago',
  'sync.agoMin': '{n} min ago',
  'sync.agoH': '{n}h ago',
  'sync.agoD': '{n}d ago',
  'sync.unknownTime': 'unknown time',

  // ---- v5.18 stragglers found during rewiring ----
  'skillmeta.cooldownAfter': 'Cooldown {n}s after returning home.',
  'roster.noSurge': 'No Surge — this creature works differently.',
  'roster.surgePrism': 'Prism Surge: +{n} to EVERY element on the next mission. {cadence}',
  'roster.dualSurge': 'Dual Surge',
  // 🎓 v7.38 (per Javi): a natural five-way tie - Prism Surge is the trained skill's name.
  'roster.surgeEvery': 'Surge: +{n} to EVERY element on the next mission. {cadence}',
  'roster.namedSurge': '{targets} Surge',
  'roster.surgeNamed': '{name}: +{n} {targets} on the next mission. {cadence}',
  'roster.momentumTip': 'Momentum ×{n}: +1 speed, +{n} ⚔️, −{n} lowest element',
  'roster.impulseTip': 'Impulse ×{n}: −1 speed, +{n} ⚔️, +{n} to its highest and lowest element',
  'roster.hibernalTip': 'Hibernal Might banks permanent stats when the rest completes',
  'forest.ffTipOn': 'Back to normal speed',
  'forest.ffTipOff': 'Fast-forward ×3',
  'app.homeTip': 'Back to the title screen',
  'app.backAgainToExit': 'Press back again to leave the game',

  'mission.showRadarTip': 'Show the stat radar big',
  'mission.showEnemyTip': 'Show the enemy big',
  'mission.bloodPactTipOn': 'The pact is sealed: 2 HP at dispatch for +10% (click to cancel)',
  'mission.bloodPactTipOff': 'Seal a Blood Pact: pay 2 HP at dispatch for +10% success',
  'mission.cursedSlotTip': 'Cursed slot: this creature loses its strongest relevant element',
  'mission.lockedSlotTip': '{name} is locked in — pick below to replace the team, or Clear Team to free it',
  'mission.removeTip': 'Remove {name}',
  'mission.emptySlotTip': 'Empty slot',
  'mission.koTip': 'Knocked out by the boss — can’t join this fight',
  'mission.knowsTip': 'A creature never learns the same skill twice',
  'mission.mirrorTip': 'The crow cannot study its own reflection',
  'mission.spentTip': 'Already fought a trial this node',
  'mission.committedPieceTip': 'Locked into another piece — open it to free them',
  'mission.committedTrialTip': 'Locked into another trial — open it to free them',
  'mission.surgeTipArmed': 'Surge armed for the next mission (click to disarm)',
  'mission.surgeTipRallying': 'Rallying Cry: its Surge is always armed — every mission it joins rallies the team',
  'mission.surgeTipSpent': 'Surge spent — recharges at the next node',
  'mission.surgeTipReadyIn': 'Surge ready in {n}s',
  'mission.surgeTipArm': 'Arm this creature’s surge for its next mission',
  'mission.discardTip': 'Clear this mission for −{n} HP; a new one will arrive',
  'mission.discardFreeTip': 'Walk away — gold missions are free to skip',
  'encounter.gauntletTip': 'The Gauntlet — what this run means (pauses)',
  'encounter.ascensionTip': 'What this Ascension means (pauses)',
  'encounter.gauntletPlainTip': 'The Gauntlet — what this run means',
  'encounter.ascensionPlainTip': 'What this Ascension means',
  'encounter.mapPlainTip': 'View the map',
  'encounter.relicsPlainTip': 'View your relics',
  'bossinspect.browse': 'Swipe or ‹ › to browse',

  // ---- 🌍 v5.19: the meta screens — title, collection, draft, daily,
  // level-up inspector and the tutorial. ----
  // 🎓 v7.38 (per Javi): normal (A0) campaign runs have no starting relic since v7.33/v7.37 -
  // the baked card is relic-free; draft.vue swaps in tutorial.draftRelic.* when the run offers one.
  'tutorial.draftIntro.title': 'Scout, then draft your team',
  'tutorial.draftIntro.b0': 'Draft creatures whose elements cover the threats ahead',
  'tutorial.draftIntro.b1': 'Tap creatures to pick them — Confirm appears once your team is full',
  'tutorial.draftIntro.b2': 'Got it opens the Route Preview',
  'tutorial.draftRelic.title': 'Scout, draft, and pick a relic',
  'tutorial.draftRelic.b1': 'Then choose a starting relic — it lasts the whole run',
  'tutorial.draftThreats': 'Your first stops threaten {list} — cover them',
  // 🗺️ v7.05 (per Javi): the card shows the stops' node icons instead of spelling out elements.
  // v7.08 (per Javi): the row is a CHOICE - you start at one of them, not all.
  'tutorial.draftThreatsNodes': 'Your first stop will be one of these — draft creatures that cover it',
  'tutorial.map.title': 'The forest road',
  'tutorial.map.b0': 'Tap a glowing node to read it, then Take this path',
  'tutorial.map.b1': '⚔️ fight · ⛺ heals · 🎁 relic · 🛒 shop · ❓ gamble · 🧩 trial',
  'tutorial.map.b2': '✏️ Plan route sketches your path',
  'tutorial.encounter.title': 'Your first fight',
  'tutorial.encounter.b0': 'Clear the ⭐ goal to finish the node',
  'tutorial.encounter.b1': 'Missions expire on their own — that costs ❤️',
  'tutorial.encounter.b2': '🗑️ discard costs the same as expiring — a fresh mission comes',
  'tutorial.encounter.b3': '🏕️ camp pauses · tap an empty spot to fast-forward',
  'tutorial.encounter.b3FirstRun': '🏕️ camp pauses · to speed up, turn on Tap to Fast Forward in the menu',
  'tutorial.banner.title': 'The banner, at a glance',
  'tutorial.banner.b0': 'Act and node up top · 🗺️ peeks at the road ahead',
  'tutorial.banner.b1': 'The clock runs only while you play',
  'tutorial.banner.b2': '⭐ node goal · 💰 gold for shops · ❤️ your life',
  'tutorial.banner.b3': 'The corner menu pauses, mutes, or ends the run',
  'tutorial.mission.title': 'Build the team',
  'tutorial.mission.b0': 'Tap creatures until every element is covered',
  'tutorial.mission.b1': 'The % is your win chance',
  'tutorial.mission.b2': 'Coverage you miss costs ❤️, win or lose',
  'tutorial.mission.b3': 'Fight! — they walk out, fight, and walk back',
  'tutorial.done.title': 'Mission complete!',
  'tutorial.done.b0': 'That is the loop: missions, coverage, resolve',
  'tutorial.done.b1': 'Level up, earn 💰 for the 🛒 shop, reach the boss',
  'tutorial.done.b2': 'Good luck out there',
  'tutorial.gold.title': 'A gold mission!',
  'tutorial.gold.b0': '💰 pays gold for the 🛒 shop',
  'tutorial.gold.b1': 'It does not count toward the node goal',
  'tutorial.gold.b2': 'Letting it expire is free — failing it is not',
  'tutorial.bossfight.title': 'Boss fight!',
  'tutorial.bossfight.b0': 'Your coverage is your win chance',
  'tutorial.bossfight.b1': 'Missing coverage hurts, win or lose',
  'tutorial.bossfight.b2': 'Read its skill before you commit',
  'tutorial.levelup.title': 'Level up!',
  'tutorial.levelup.b0': 'A creature has a stat point waiting — it glows',
  'tutorial.levelup.b1': 'Spend it where the road ahead threatens',
  'tutorial.levelup.b2': 'Banking it for later is fine',
  'tutorial.failure.title': 'That one hurt',
  'tutorial.failure.b0': 'Damage scales with the coverage you missed',
  'tutorial.failure.b1': 'It lands win or lose',
  'tutorial.failure.b2': 'Expired missions chip ❤️ too, at half',
  'tutorial.failure.b3': 'At 0 ❤️ the run ends',
  'tutorial.puzzle.title': 'A trial of mirrors',
  'tutorial.puzzle.b0': 'Trials are dealt from your own team’s stats',
  'tutorial.puzzle.b1': 'A clean split exists — each creature joins one',
  'tutorial.puzzle.b2': 'Lock every trial, then ⚔️ Begin',
  'tutorial.puzzle.b3': 'No timers — damage taken is your score',
  'tutorial.enrage.title': 'The horde turns furious',
  'tutorial.enrage.b0': 'After nine missions, new arrivals enrage',
  'tutorial.enrage.b1': 'Every second they hit harder',
  'tutorial.enrage.b2': 'The 😡 pill shows the climb',
  'tutorial.enrage.b3': 'Clear the ⭐ goal fast — stalling only feeds it',
  'tutorial.training.title': 'The Training Grounds',
  'tutorial.training.b0': 'Send exactly one creature',
  'tutorial.training.b1': 'Pick one of three skills — theirs forever',
  'tutorial.training.b2': 'No roll, no damage — just survive the node',
  'tutorial.training.b3': 'Short fuse — commit early',
  'tutorial.skip': 'Skip tutorial',
  // 🎓 v7.04 First Dispatch coaching + the guide door.
  'firstDispatch.skip': 'Skip practice',
  // 🎓 v7.40 (per Javi, D2/D5): the practice storyboard - one short line per
  // step beside what it names, verb first, element names written out, and
  // every number read off the lesson and the creature (never hard-coded).
  // 🦁 v7.44 (per Javi: more personality): Cinderfang says them, in his own
  // voice ("my card", "we won") - the no-break space keeps "have 1." together.
  // 🦁 v7.45 (per Javi: v7.44's were "too cringe/childish"): an easygoing
  // teammate - plain, mostly periods, no bragging, cheering or catchphrases.
  'practice.meetMe': 'Hi, I’m {name}, your {element} creature.',
  'practice.meetFriend': 'This is {name}, our new {element} teammate.',
  'practice.says': '{name} says',
  'practice.tapMonster': 'Tap the {element} monster. It matches me.',
  'practice.tapMyCard': 'Now tap my card to send me.',
  'practice.tapCreature': 'Now tap {name}.',
  'practice.sureWinFight': 'That’s a sure win. Tap Fight.',
  'practice.mightLoseFight': 'We might lose. Tap Fight and see.',
  // 🦁 v7.47 (per Javi: "watch me walk" sounds lame): the walk teaches the travel time instead.
  'practice.watchGo': 'On my way. The fight starts when I arrive.',
  'practice.watchThem': 'On our way. The fight starts when we arrive.',
  'practice.youWon': 'We won! Tap Continue when you’re ready.',
  'practice.youWonTeam': 'We won! It took both of us.',
  'practice.lookNeeds': 'It needs {n} {element}. I only have\u00a0{m}.',
  'practice.letItGo': 'Let the {element} monster go. Watch its countdown.',
  'practice.cantWin': 'Too much {element} for me. Let it go.',
  'practice.gotAway': 'Good call. It only cost us {n} health.',
  'practice.tapNewMonster': 'Tap the new monster. It needs us both.',
  'practice.addBoth': 'Tap both cards. It needs {a} and {b}.',
  'practice.mightLoseAdd': 'We might lose. Add {name} for {element}.',
  'practice.mightLoseAddMe': 'We might lose. Add my card for {element}.',
  'practice.retryMonster': 'Let’s try that again. Tap the {element} monster.',
  'practice.retryNew': 'Let’s try that again. Tap the new monster.',
  // 🎓 v8.02 (per Javi): the Surge lesson, then Rillhopper's Skim and the glass.
  'practice.surgeCard': 'It needs more Fire. My Surge adds two.',
  'practice.tapSurgeMonster': 'Tap the Fire monster. It needs {n}.',
  'practice.tapSurge': 'Now tap Surge. That adds {n} {element}.',
  'practice.skillCard': '{name} uses a skill instead of Surge.',
  'practice.tapSkillMonster': 'Tap the Water monster. It suits {name}.',
  'practice.tapGlass': 'Tap the glass on {name} to read it.',
  // 🎓 v8.05 (per Javi): the page names the skill to read, and its button says how to go on.
  'practice.readPage': 'Every creature has a page: {skill}, then passive.',
  'practice.backToMission': 'Back to the mission',
  'practice.tapSkill': 'Now tap Skim. The trip takes no time.',
  'practice.watchSkim': 'No trip at all. Straight into the fight.',
  'practice.youWonSkim': 'We won! {name} is already home.',
  'practice.done': 'That’s the basics. Now pick your own team.',
  'practice.pickTeam': 'Pick my team →',
  'practice.healthLoss': '−{n} health',
  'practice.sureWin': 'Sure win!',
  'practice.mightLose': 'Might lose!',
  'practice.skipAsk': 'Skip the practice? You can replay it later.',
  'practice.keepPracticing': 'Keep practicing',
  'practice.skipYes': 'Skip',
  'practice.label': 'Practice tip',
  // 🎓 v7.41 (per Javi, §3 C): the first run's guide through location 1 (English only).
  'coach.label': 'Tip',
  'coach.arrive': 'Beat {n} monsters to win here!',
  'coach.tap': 'Tap the {element} monster',
  'coach.tapTwo': 'Tap the {a} and {b} monster',
  'coach.nowTap': 'Now tap the {element} monster',
  'coach.nowTapTwo': 'Now tap the {a} and {b} monster',
  'coach.noTap': 'No tap needed',
  'coach.needsAdd': 'Needs {n} {element}. You have {m}. Add more!',
  'coach.needs': 'Needs {n} {element}. You have {m}.',
  // 🎓 v7.41 (review): the sheet names who to tap next (an empty sheet said nothing).
  'coach.pick': 'Tap {name}: it has {element}!',
  'coach.cantWin': 'Can’t win this one. Let it go.',
  'coach.cantWinYet': 'Can’t win this one yet. Wait for your team.',
  // 🎓 v7.41 (review: discard was taught nowhere) - the sheet's trash can; it costs what letting it go does.
  'coach.discard': 'Can’t win it? Tap the trash can.',
  'coach.wheel': 'The wheel spins. Green means you win!',
  'coach.surge': 'Tap Surge: +{n} {element} for this fight!',
  'coach.gold': 'Beat it for gold, or skip it.',
  'coach.plus': 'Tap +{n} to make it stronger!',
  'coach.pause': 'Tap camp to pause anytime',
  // 😏 v7.47 (per Javi): plain - the monster's own line (taunt.*) is the mischief.
  'coach.expiry': 'It got away. You lost {n} health.',
  'coach.enrage': 'Hurry! Monsters hit harder over time.',
  // ⏩ v7.42: the whole team out, tap-to-fast-forward still off - the menu's switch.
  'coach.ffMenu': 'Speed up? Turn on fast forward in the menu.',
  // 🎓 v7.41 (review): ×3 on while a monster the team can win waits (the #1 killer, F02).
  'coach.cleared': 'Location cleared! ⭐ {n}/{m}',
  'coach.prize': 'Pick one prize!',
  'coach.hide': 'hide',
  // 😏 v7.47 (per Javi: "quiet mischief", English only): what the monsters say
  // (data/taunts.ts picks the key, utils/tauntFloor.ts decides when). Polite
  // tricksters: a period or a question mark, never "!", no sound effects, no
  // catchphrases; one leaving talks about itself, never about the player.
  // The first line of each three is the strongest (shown first).
  'taunt.fire.appear.1': 'Mind the sparks.',
  'taunt.fire.appear.2': 'Is it warm in here?',
  'taunt.fire.appear.3': 'Running a little hot today.',
  'taunt.fire.away.1': 'Up in smoke.',
  'taunt.fire.away.2': 'Too hot to hold.',
  'taunt.fire.away.3': 'Off to cool down.',
  'taunt.water.appear.1': 'Pardon the drips.',
  'taunt.water.appear.2': 'Here comes the tide.',
  'taunt.water.appear.3': 'Nice day for a swim.',
  'taunt.water.away.1': 'Slipped right through.',
  'taunt.water.away.2': 'Caught the current.',
  'taunt.water.away.3': 'Water under the bridge.',
  'taunt.grass.appear.1': 'Look who sprouted.',
  'taunt.grass.appear.2': 'Hiding in plain sight.',
  'taunt.grass.appear.3': 'Blending in nicely.',
  'taunt.grass.away.1': 'Somewhere in the green.',
  'taunt.grass.away.2': 'Back into the bushes.',
  'taunt.grass.away.3': 'Now I’m a hedge.',
  'taunt.lightning.appear.1': 'Fully charged.',
  'taunt.lightning.appear.2': 'Only stopping by.',
  'taunt.lightning.appear.3': 'Don’t blink.',
  'taunt.lightning.away.1': 'Back to the clouds.',
  'taunt.lightning.away.2': 'Gone in a flash.',
  'taunt.lightning.away.3': 'And off I zip.',
  'taunt.earth.appear.1': 'Don’t mind me.',
  'taunt.earth.appear.2': 'Just rolling in.',
  'taunt.earth.appear.3': 'Nothing moves a rock.',
  'taunt.earth.away.1': 'Back underground.',
  'taunt.earth.away.2': 'Taking the low road.',
  'taunt.earth.away.3': 'Slow, but gone.',
  'taunt.gold.appear.1': 'Shiny, isn’t it?',
  'taunt.gold.appear.2': 'Found this lying around.',
  'taunt.gold.appear.3': 'Finders keepers.',
  'taunt.gold.away.1': 'I’ll keep this, thanks.',
  'taunt.gold.away.2': 'The gold comes too.',
  'taunt.gold.away.3': 'Back in my pocket.',
  'taunt.ambush.1': 'Fancy meeting you here.',
  'taunt.ambush.2': 'Been here all along.',
  'taunt.ambush.3': 'Surprise.',
  // The eight elite champions, one line each (keyed by the codex slug).
  'taunt.champion.magma-behemoth': 'Sorry about the floor. Lava.',
  'taunt.champion.tempest-leviathan': 'Bring an umbrella. A big one.',
  'taunt.champion.the-sunken-king': 'You may bow. Or not.',
  'taunt.champion.avalanche-alpha': 'Snow day. You’re welcome.',
  'taunt.champion.the-verdant-maw': 'The garden grew. A lot.',
  'taunt.champion.stormcrown-wyrm': 'Like the crown? Real lightning.',
  'taunt.champion.the-ruin-colossus': 'Ancient. Enormous. Unbeatable. Probably.',
  'taunt.champion.the-hollow-titan': 'Hollow inside. Solid everywhere else.',
  // The practice's monster dealt again (a lesson lost, let go or sent away).
  'taunt.retry': 'Round two, then.',
  'gamemenu.stopGuidance': 'Stop guidance',
  'gamemenu.guidanceOff': 'Guidance off.',
  'gamemenu.undo': 'Undo',
  'guide.playPractice': 'Play the practice',
  'guide.practiceBusy': 'Finish or abandon your run to play the practice again.',
  'onboarding.restoreError': 'Your saved run could not be restored. Please reload and try again.',
  'onboarding.restoreAccountError': 'This saved run belongs to another account. Sign in to it to get it back.',
  'guide.title': 'How to play',
  'guide.loop1': 'Missions appear in the forest, each asking for elements.',
  'guide.loop2': 'Dispatch creatures whose stats cover what a mission asks.',
  'guide.loop3': 'They travel, fight and resolve on the wheel — clear the goal to move on.',
  'guide.odds': 'Partial coverage can still win — but every missing point costs ❤️, even on a win.',
  'guide.surge': 'Surge adds +2 of a creature’s main element to its next mission. Some creatures carry a skill instead. The glass on a card opens its page.',
  'guide.controls': '🏕️ Tap camp to pause · with Tap to Fast Forward on (in the menu), tap an empty spot for ×3 · 🗑️ discarding costs the same as letting a mission expire, and a fresh one comes.',

  // ---- Daily silhouette ----
  'daily.title': 'Daily Silhouette',
  'daily.today': 'Today',
  'daily.whoIsThis': 'Who is this?',
  'daily.guessesLeft': '{n} guesses left',
  'daily.guessLeft': '1 guess left',
  'daily.bonusRound': 'Bonus round — which is its true statline?',
  'daily.solvedOn': 'Solved on guess {n}',
  'daily.statlineNailed': ', statline nailed',
  'daily.gotAway': 'It got away — tomorrow’s another creature.',
  'daily.gotAwayThatDay': 'It got away that day.',
  'daily.playedElsewhere': 'Played on another device that day.',
  'daily.notPlayed': 'Not played.',
  'daily.banked': 'banked',
  'daily.bankedToday': 'banked today',
  'daily.waking': 'Waking today’s creature…',
  'daily.playedElsewhereToday': 'Today’s daily was already played on another device — the rewards and streak live there. Come back tomorrow!',
  'daily.nextIn': 'Next silhouette in {time}',
  'daily.dayStreak': 'Day {n}',
  'daily.openBooster': 'Open your streak booster',
  'daily.prevDay': 'Previous day',
  'daily.nextDay': 'Next day',
  'daily.milestoneStardust': '+{n} stardust',
  'daily.milestoneBooster': 'a free booster pack',

  // ---- Level-up / dossier inspector ----
  'levelup.skillsPassives': 'Skills & Passives',
  'levelup.tapChart': 'Tap an element on the chart to stage a point — the − takes one back.',
  'levelup.pts': '+{n} pts',
  'levelup.dormant': 'Dormant — its nature is no longer its highest element.',
  'levelup.awakensAt': 'Awakens at Lv {n}.',
  'levelup.surgeLine': '+{n} {elements} on its next mission',
  'levelup.fightTime': ' (−{n}% fight time)',
  'levelup.creaturePassive': 'Creature passive',
  'levelup.showChartTip': 'Show the stat radar big',
  'levelup.showCreatureTip': 'Show the creature big',

  // ---- Title screen (index) ----
  'title.signOut': 'Sign out',
  // 👤 v6.43: the alias picker and the report flag.
  'alias.title': 'Choose your name',
  'alias.titleChange': 'Change your name',
  'alias.body': 'This is what the leaderboards show instead of your account name. You can change it once a week.',
  'alias.placeholder': 'Your name',
  'alias.hint': '{min} to {max} characters: letters, numbers, spaces and . _ -',
  'alias.rules': '{min} to {max} characters: letters, digits, spaces, periods, underscores and hyphens. One rename every {days} days.',
  'alias.save': 'Save',
  'alias.later': 'Later',
  'alias.change': 'Change name',
  'alias.err.short': 'Too short.',
  'alias.err.long': 'Too long.',
  'alias.err.chars': 'Only letters, numbers, spaces and . _ -',
  'alias.err.reserved': 'That name is reserved.',
  'alias.err.blocked': 'That name is not allowed.',
  'alias.err.taken': 'That name is taken.',
  'alias.err.cooldown': 'You can change your name again in a few days.',
  'alias.err.offline': 'Could not reach the server. Try again later.',
  // 👤 v6.45: the Profile hub — avatar, name, title.
  'profile.guest': 'Not signed in',
  'profile.noTitle': 'No title',
  'profile.pickTitle': 'Title',
  'profile.pickAvatar': 'Avatar',
  'profile.noAvatar': 'No avatar',
  'profile.avatarOwned': 'a creature in your collection',
  'profile.avatarNote': '{name}: {how}',
  'profile.titleNote': '{name}: {how}',
  'profile.noTitlesYet': 'Titles come from achievements — earn one and it appears here.',
  'profile.noAvatarsYet': 'Creatures you have recruited or own can be your avatar.',
  'profile.signInHint': 'Sign in to pick a name, a title and an avatar for the leaderboards.',
  'profile.err.unknown': 'That choice is not available.',
  'profile.err.locked': 'Not unlocked on this account yet — give it a moment to sync and try again.',
  'profile.err.offline': 'Could not reach the server. Try again later.',
  // 🏆 v6.45: the Achievements shelf.
  'ach.count': '{n} of {total} earned',
  'ach.earned': 'Earned',
  'ach.inProgress': 'In progress',
  'ach.hidden': 'Hidden',
  'ach.hiddenNote': '{n} secret achievements. Their names appear when you earn them.',
  'ach.rewardTitle': 'Title: {title}',
  'ach.rewardAvatar': 'Avatar:',
  'ach.avatarUnlocked': 'New avatar:',
  'ach.titleUnlocked': 'New title: {title}',
  'ach.reviewNote': 'Secret achievements are shown for review on this device.',
  'ach.secretTag': 'secret',
  'board.reportTip': 'Report this name',
  // 📄 v6.46: the paged board.
  'board.yourPlace': 'Your place',
  'board.pageOf': 'Page {n} of {total}',
  'board.notRanked': 'You are not in the top {n} yet.',
  'board.reported': 'Reported',
  'auth.signInTimeout': 'Sign-in took too long. Close the account picker and try again. If it stays stuck, restart the app.',
  'auth.signInFailed': 'Sign-in did not finish. Please try again.',
  'nativeIdentity.title': 'Keep your progress',
  'nativeIdentity.earn': 'Sign in to keep your Stardust and progress safe if you change or lose your phone.',
  'nativeIdentity.recovery': 'Your previous sign-in could not be restored. Sign in to recover your account. New earnings will stay queued.',
  'nativeIdentity.later': 'Not now',
  'title.signIn': 'Sign in',
  'title.signInApple': 'Sign in with Apple',
  'title.signInGoogle': 'Sign in with Google',
  'title.signInWhy': 'to join the leaderboard',
  // 🎓 v7.40 (D4): a newcomer's calm title has no Leaderboard door yet.
  'title.signInWhyCalm': 'to save your progress',
  'title.signInToAppear': 'Sign in to appear here.',
  'title.tabExpedition': 'Expedition',
  'title.tabPuzzle': 'Puzzle',
  'title.tabGauntlet': 'Gauntlet',
  'title.tabAchievements': 'Trophies',
  'title.lbAchievements': 'Trophies — achievements earned; the most wins, and at a tie whoever got there first.',
  'title.lbEmptyAchievements': 'Nobody has posted their trophies yet. Earn one and be the first!',
  'title.lbAchievementsCount': '{n} of {total}',
  'title.lbExpedition': 'Farthest expedition — beat your best and it updates on your next run’s end.',
  'title.lbPuzzle': 'Puzzle gauntlet — 25 − highest level + total damage; 0 is a flawless full clear, LOWEST wins.',
  'title.lbGauntlet': 'The Gauntlet — endless acts past Ascension 10. DEEPEST act wins; nodes cleared break ties.',
  'title.lbUnreachable': 'The leaderboard is unreachable right now — try again in a bit.',
  'title.lbEmptyRuns': 'Nobody has posted a run yet. Be the first!',
  'title.lbEmptyGauntlet': 'Nobody has survived past the summit yet. Be the first!',
  'title.lbEmptyPuzzle': 'Nobody has posted a puzzle score yet. Be the first!',
  'title.lbActNodes': 'Act {act} · {n} nodes',
  'title.lbVictory': '{asc} · Victory',
  'title.tabDaily': 'Weekly',
  'title.lbDaily': 'This week’s seeded expedition ({code} · A{n}) — unlimited attempts, best run counts.',
  'title.lbEmptyDaily': 'No expeditions this week yet — be the first.',
  'title.lbDailyVictory': 'Victory',
  'menu.stats': 'Player Stats',
  'stats.title': 'Player Stats',
  'stats.campaign': 'Expeditions',
  'stats.weekly': 'Weekly Expeditions',
  'stats.gauntlet': 'The Gauntlet',
  'stats.started': 'Started',
  'stats.won': 'Won',
  'stats.lost': 'Lost',
  'stats.abandoned': 'Abandoned',
  'stats.winRate': 'Win rate',
  'stats.streaks': 'Win Streaks',
  'stats.streakAsc': 'Ascension {n}',
  'stats.streakBest': 'best {n}',
  'stats.missions': 'Missions',
  'stats.succeeded': 'Succeeded',
  'stats.failed': 'Failed',
  'stats.expired': 'Expired',
  'stats.handledRate': 'Handled rate',
  'stats.journey': 'The Journey',
  'stats.playtime': 'Total playtime',
  'stats.ascension': 'Highest ascension unlocked',
  'stats.owned': 'Creatures owned',
  'stats.puzzleBest': 'Puzzle Game best',
  'stats.note': 'Your private ledger — counting since v5.64, never on the leaderboard.',
  'menu.dailyStart': 'Weekly Expedition',
  'menu.dailyContinue': 'Continue Weekly',
  'menu.dailyInfo': '{code} · Ascension {n} · same run for everyone this week',
  'menu.weeklyVanilla': 'A clean week — no special law. Pure skill, pure seed.',
  'menu.dailyPrize': 'Weekly podium! Rank {rank} paid +{n} stardust',
  // 🥇 v7.79: the Weekly board's prize beside a podium rank (English only until Javi asks for es/fr).
  'board.prizeTip': 'Wins {n} Stardust when the week closes',
  // 🗓️ v7.80 (per Javi): when the Weekly resets - {time} a countdown, {when} the viewer's own day and time. English only.
  'menu.weeklyResets': 'Resets in {time} · {when}',
  'menu.weeklyResetsSoon': 'Resets in under a minute · {when}',
  'title.lbProgress': '{asc} · Act {act} · {n} nodes',
  'title.pts': '{n} pts',
  'title.puzzleHeading': 'Puzzle Game',
  'title.puzzleIntro': '{n} fixed brain-teasers — the same for every player. Each level deals you a set party and a set of trials: split the team so every trial is covered. A perfect split takes zero damage. Clear a level to unlock the next.',
  'title.yourScore': 'Your score:',
  'title.scoreTail': ') — 0 is perfect, lowest wins.',
  'title.levelN': 'Level {n}',
  'title.partyOf': 'Party of {n}',
  'title.perfect': 'perfect',
  'title.replay': 'Replay',
  'title.play': '▶ Play',
  'title.resetConfirm': 'Really wipe all bests and locks? Tap again.',
  'title.resetProgress': '↺ Reset progress',
  'title.streakDays': '{n}-day streak',
  'title.todayDone': 'today ✓ +{n}',
  'title.todayWaiting': 'today’s silhouette waits — play it',
  'title.daysTo': '{n}d to {label}',
  'title.openDailyTip': 'Open the daily',
  'title.best': 'BEST +{n}%',
  'title.plusPct': '+{n}%',
  'title.boosterName': 'Booster Pack',
  'title.boosterText': '{n} random creatures. Duplicates refund',
  'title.boosterTextTail': ', by rarity.',
  'title.oddsPerCard': 'Odds per card:',
  'title.owned': '✓ Owned — always ready in your draft',
  'title.abandonTitle': 'Abandon current run?',
  'title.abandonBody': 'Starting a new run abandons the one in progress — its team, relics and map progress are gone for good.',
  'title.abandonYes': 'Abandon & start new',
  'title.abandonNo': 'Keep my run',
  'title.howToPlay': 'How to play',
  'title.how1': 'Draft a team — preview the route first.',
  'title.how2': 'Missions appear with elemental requirements and timers.',
  'title.how3': 'Dispatch creatures whose stats cover the requirements.',
  'title.how4': 'Missing coverage and lapsed missions cost ❤️ — at 0 the run ends.',
  'title.how5': 'Level up, collect rewards, and beat the boss at the end of each act.',
  'title.reference': '📚 Reference — relics, rewards, bosses, creatures',
  'title.relicList': 'List of Relics ({n} / {m} collected)',
  'title.rewardList': 'List of Rewards ({n} / {m} collected)',
  'title.bossList': 'Bosses ({n} / {m} fought)',
  'title.creatureList': 'Creature Collection ({n} / {m} recruited)',
  'title.skillList': 'On-Use Skills ({n})',
  'title.trainedList': 'Trained Skills ({n} / {m} learned)',
  'title.passiveList': 'Passives ({n} / {m} awakened)',
  'title.ascensionList': 'Ascensions ({n})',
  'title.provisionalNote': 'Rarity is provisional (simulation-based) and cosmetic for now. Grayed entries haven’t been collected/chosen in a run yet.',
  'title.bossNote': 'Each run draws three DIFFERENT bosses from this roster, one per act. Grayed bosses haven’t been faced yet. Tap one to see it on its pedestal — then swipe or ‹ › to browse the roster.',
  'title.creatureNote': 'Surge skills are fixed by each creature’s base statline. Grayed creatures haven’t joined a team yet.',
  'title.creatureSkillsHead': 'Creature Skills',
  'title.trainedSkillsHead': 'Trained Skills',
  'title.skillNote': 'Creature skills replace their carrier’s Surge; trained skills come from the Training Ground.',
  'title.passiveNote': 'A creature awakens the passive of its main element at level 5 — but only if that element is still tied for its highest stat. Train off-elements past the main and the awakening is lost until the main catches back up.',
  'title.trainedNote': 'Learned at 🎓 Training Grounds missions — a long, riskless side mission that appears once per act. Send one creature, pick 1 of 3 skills, survive the node. Grayed skills haven’t been learned yet.',
  'title.ascensionNote': 'Win the run to unlock Ascension 1; each further win unlocks the next level. Every ascension brings injuries back — and the cures (Field Dressing, Trail Medkit, Miracle Salve) and injury relics (Medic Band, Close Enough) with them.',
  'title.creaturePassiveTag': 'Creature passive',
  'title.tierLine': 'Tier {tier} (Lv {lv}):',
  'title.speedN': 'Speed {n}',
  'title.fightN': 'Fight {n}',
  'title.fightPlusN': 'Fight +{n}',
  'title.tapToOpen': 'Tap to open!',
  'title.duplicateRefund': 'Duplicate! +{n}',
  'title.newBang': 'NEW!',
  'title.dustReturned': '+{n} stardust returned!',
  'title.rewardPoolBattle': 'Battle Rewards',
  'title.rewardPoolRest': 'Rest Rewards',
  'title.changelogHead': 'Changelog — {version}',
  'title.surgeShort': 'Surge: +{n} {elements}',
  'title.dualSurgeShort': 'Dual Surge: +{n} {elements}',
  'title.lvLine': 'Lv {n}: {text}',
  'title.lvPassiveLine': 'Lv {n} passive: {text}',
  'title.timeD': '{d}d {h}h',
  'title.timeH': '{h}h {m}m',
  'title.timeM': '{m}m',
  'title.wipeConfirm': 'Wipe this account’s stardust, collection AND progress (ascension, codex, run)? This cannot be undone.',
  'title.wipeFailed': 'Reset failed — are you signed in and online?',

  // ---- Collection page ----
  'coll.hint2': 'Everything you’ve met fills in — tap a found entry for its dossier. Fresh discoveries glow until you open them. Creatures you’ve only borrowed for a run stay in black and white until you buy them.',
  'coll.counts': '{owned} owned · {met} met / {total}',
  'coll.shown': '· {n} shown',
  'coll.new': '✨ New',
  'coll.missing': 'Missing',
  'coll.collected': 'Collected',
  'coll.sortAzTip': 'Sort these alphabetically',
  'coll.sortDefaultTip': 'Back to the default order',
  'coll.nothingMatches': 'Nothing matches this filter.',
  'coll.showAll': 'Show all',
  'coll.borrowedTip': '{name} — borrowed, not owned',
  'coll.threat': '{element} threat',
  'coll.threatBody': 'A wild menace of the {element} trails — cover its elements or pay in HP.',

  // ---- Draft page ----
  'draftui.answeredCall': '{n} answered today’s call — pick any {m} (chosen {x} / {m}).',
  'draftui.assemble': 'Assemble Your Team',
  'draftui.routeScroll': 'Route Preview — scroll the whole climb before you pick',
  'draftui.routeInspect': 'Route Preview — tap a node to inspect it before you commit',
  'draftui.pickRelic': 'Pick 1 of {n} to define your run.',
  'draftui.ready': 'Your Expedition Is Ready!',
  'draftui.finalRoster': 'Final Roster',
  'draftui.teamCount': 'Your Team ({n})',
  'draftui.removeTip': '{name} — tap to remove',
  // ⭐ v7.57: the draft dock's captain seat (English only).
  'draftui.captainTip': '{name}, your captain: it speaks for the team in the story scenes. Tap to remove.',
  'draftui.captainSeat': 'Your first pick is your captain: it speaks for the team in the story scenes.',

  // ---- 🎓 v7.41 (per Javi, proposal §3 C): the first run's draft + map guide ----
  // English only (Javi, 2026-09-13). Plain words (D2): location, never node.
  'firstrun.trayLabel': 'Starting locations',
  'firstrun.markTick': '{element}: ready',
  'firstrun.markLow': '{element}: a little',
  'firstrun.markNone': '{element}: none',
  'firstrun.matchBadges': 'Match your creatures to these badges',
  'firstrun.pickFor': 'Pick {name}: it has {element}!',
  'firstrun.confirmMatches': 'Tap Confirm Team: your team matches!',
  'firstrun.confirm': 'Tap Confirm Team',
  'firstrun.gate': 'Your team can’t beat these yet. Swap one?',
  'firstrun.swapIn': 'Swap in {name}',
  'firstrun.keepTeam': 'Keep my team',
  'firstrun.goHere': 'Go here: your team matches!',
  'firstrun.goHereBest': 'Go here: your team matches best!',
  'firstrun.takePath': 'Tap Take this path!',
  'firstrun.askNone': 'Your team has no {element}. Go anyway?',
  'firstrun.askLow': 'Your team has little {element}. Go anyway?',
  'firstrun.goAnyway': 'Go anyway',
  'firstrun.pickAnother': 'Pick another',
  // A first run's map card and hint (review, D2): location, never node. Veterans keep theirs.
  'tutorial.map.b0FirstRun': 'Tap a glowing location, then Take this path',
  'mappage.tapGlowingFirstRun': 'Tap a glowing location to see it.',
  // ...and its location card's goal, in the coach's words ("Beat 3 monsters to win here!") - the rails' span (locations 1-2).
  'node.beatN': 'Beat {n} monsters',
  'node.beatOne': 'Beat 1 monster',

  // ---- Boss intro ----
  'bossintro.title': 'The Bosses Ahead',
  'bossintro.body': 'Three stand between you and the forest’s end. Draft accordingly.',
  'bossintro.tapInspect': 'tap to inspect',
  'bossintro.power': 'Power: ???',
  'bossintro.toDraft': 'To the Draft',
  'bossintro.tapContinue': 'tap anywhere to continue',

  // ---- Misc components ----
  'music.mute': 'Mute music',
  'music.unmute': 'Unmute music',
  'clock.timeInRun': 'Time in run',
  'pings.offscreen': 'Mission off-screen — tap to look',
  'card.travelSpeed': 'Travel speed',
  'card.provisional': '{rarity} (provisional)',
  'chips.fromRelics': ' (+{n} from relics/surge)',
  'chips.fromInjuries': ' (−{n} from injuries/exhaustion)',
  'radar.chartLabel': 'Element stats chart',
  'radar.plusOne': '+1 {element}',
  'radar.takeBack': 'Take back a staged point',
  'gauntletWord': 'Gauntlet',
}

/** 🌍 v6.63 (Lighthouse pass 4): the dictionaries are LAZY. Only English
 * ships in the entry chunk; app/data/lang/es.ts and fr.ts are chunks that
 * plugins/locale.client.ts loads and registers HERE before the app mounts
 * when the player's language is one of them (useLocale.setLocale loads on
 * a switch). `en` is always present - it is the fallback. */
export const MESSAGES: { en: Messages } & Partial<Record<Locale, Messages>> = { en }

/** Every key the UI can ask for — English is the source of truth. */
export const MESSAGE_KEYS = Object.keys(en)
